'use client'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, ExternalLink } from 'lucide-react'
import { Separator } from '@radix-ui/react-separator'
import { PrivacyPolicyModal } from '@/app/(marketing)/hero/components/PrivacyPolicyModal'
import { TermsOfServiceModal } from '@/app/(marketing)/hero/components/TermsOfServiceModal'
import { ContactUsModal } from '@/app/(marketing)/hero/components/ContactUsModal'
import { HowWeElevateHempModal } from '@/app/(marketing)/hero/components/HowWeElevateHempModal'

// Footer section component for better organization
interface FooterSectionProps {
  title: string
  titleColor?: string
  children: React.ReactNode
}

function FooterSection({ title, titleColor = 'text-white', children }: FooterSectionProps) {
  return (
    <div className="space-y-5">
      <h4 className={`font-semibold text-base ${titleColor} mb-2 relative group`}>
        {title}
        <div className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-current to-transparent transition-all duration-300 group-hover:w-12`}></div>
      </h4>
      {children}
    </div>
  )
}

// Footer link component with enhanced hover effects
interface FooterLinkProps {
  href: string
  children: React.ReactNode
  external?: boolean
  hoverColor?: string
}

function FooterLink({ href, children, external = false, hoverColor = 'hover:text-white' }: FooterLinkProps) {
  return (
    <Link 
      href={href} 
      className={`text-white/70 text-sm ${hoverColor} transition-all duration-300 hover:translate-x-1 block group flex items-center justify-between`}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <span>{children}</span>
      {external && <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />}
      <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-1" />
    </Link>
  )
}

// CTA Card component
function FooterCTA() {
  return (
    <div className="glass-card p-8 bg-gradient-to-br from-eskerium-blue/10 via-eskerium-navy/20 to-eskerium-gold/10 border border-eskerium-gold/20">
      <div className="text-center space-y-4">
        <h3 className="text-2xl font-bold text-eskerium-gold mb-3">Ready to elevate your products?</h3>
        <p className="text-hierarchy-2 mb-6 leading-relaxed">
          Book your discovery call and explore custom solutions tailored to your brand.
        </p>
        <Link
          href="/contact"
          className="inline-flex items-center gap-2 px-6 py-3 bg-eskerium-gold text-eskerium-dark font-semibold rounded-lg hover:bg-eskerium-gold/90 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-eskerium-gold/25"
        >
          Schedule Discovery Call
          <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  )
}

// Trust indicators component
function TrustIndicators() {
  const indicators = [
    { label: 'Trusted by industry leaders', color: 'status-dot-blue' },
    { label: 'Premium quality guaranteed', color: 'status-dot-gold' },
    { label: 'Compliance-ready solutions', color: 'status-dot-green' }
  ]

  return (
    <div className="space-y-3">
      {indicators.map((indicator, index) => (
        <div key={index} className="flex items-center space-x-3 text-hierarchy-2 group">
          <div className={`status-dot ${indicator.color} animate-pulse group-hover:scale-110 transition-transform duration-200`}></div>
          <span className="group-hover:text-white transition-colors duration-200">{indicator.label}</span>
        </div>
      ))}
    </div>
  )
}

export default function Footer() {
  return (
    <footer className="relative glass-footer text-white overflow-hidden">
      {/* Enhanced background effects with aurora and grid */}
      <div className="absolute inset-0">
        {/* Aurora gradient background */}
        <div className="absolute inset-0 bg-aurora opacity-15"></div>
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-grid-pattern bg-grid-pattern opacity-5"></div>
        {/* Enhanced gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-eskerium-blue/5 via-transparent to-eskerium-gold/5"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(61,120,237,0.08),transparent_70%)]"></div>
        {/* Static accent elements */}
        <div className="absolute top-20 left-1/4 w-64 h-64 bg-gradient-to-r from-eskerium-blue/5 to-eskerium-gold/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-1/4 w-48 h-48 bg-gradient-to-r from-eskerium-gold/5 to-eskerium-blue/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="section-container py-20 md:py-28 relative z-10">
        {/* Main Footer Content: 3-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mb-16 items-start">
          {/* Column 1: Brand text */}
          <div className="space-y-8">
            <div className="text-center lg:text-left space-y-6">
              <h2 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                <span className="bg-gradient-to-r from-eskerium-gold via-yellow-300 to-eskerium-gold bg-clip-text text-transparent">
                  Gold Standard Live Resin
                </span>
              </h2>
              <p className="text-xl text-hierarchy-2 font-light italic mb-8 leading-relaxed">
                <span className="text-eskerium-gold">Your competitive edge</span> in premium hemp extracts.
              </p>
            </div>         
          </div>

          {/* Column 2: Buttons stacked, left-aligned within column and constrained */}
          <div>
           <div className="flex flex-col gap-4 items-start max-w-sm">
              <ContactUsModal>
                <button className="group relative inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-eskerium-gold to-eskerium-gold/90 text-eskerium-dark font-medium rounded-xl hover:from-eskerium-gold/90 hover:to-eskerium-gold transition-all duration-300 ease-gentle hover:scale-105 hover:-translate-y-1 hover:shadow-elev-2 overflow-hidden">
                  <span className="relative z-10">Contact us</span>
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </ContactUsModal>
              <HowWeElevateHempModal>
                <button className="group card-glass border border-white/20 hover:border-eskerium-blue/50 text-white hover:bg-white/10 inline-flex items-center gap-3 px-6 py-3 rounded-xl font-medium transition-all duration-300 ease-gentle hover:scale-105 hover:-translate-y-1">
                  How We Elevate Hemp
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </HowWeElevateHempModal>
            </div>
          </div>

          {/* Column 3: Trust indicators aligned to right edge (container alignment) */}
          <div className="flex lg:justify-end">
            <div className="mb-16 text-right">
              <TrustIndicators />
            </div>
          </div>
        </div>



        {/* Copyright and Legal - Enhanced */}
        <div className="border-t border-white/10 pt-8 space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-sm text-white/60">
              © {new Date().getFullYear()} Eskerium Biosciences. All rights reserved.
            </p>
            <div className="flex items-center space-x-8 text-sm text-white/60">
              <Link 
                href="/terpenes"
                className="hover:text-white transition-colors duration-300 hover:scale-105 flex items-center gap-1"
              >
                Terpenes
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </Link>
              <PrivacyPolicyModal>
                <button className="hover:text-white transition-colors duration-300 hover:scale-105">
                  Privacy Policy
                </button>
              </PrivacyPolicyModal>
              <TermsOfServiceModal>
                <button
                  className="hover:text-white transition-colors duration-300 hover:scale-105"
                  data-terms-modal
                >
                  Terms of Service
                </button>
              </TermsOfServiceModal>
              <ContactUsModal>
                <button className="hover:text-white transition-colors duration-300 hover:scale-105">
                  Contact
                </button>
              </ContactUsModal>
            </div>
          </div>
          
          {/* Enhanced FDA Disclaimer with glass treatment */}
          <div className="card-glass p-6 border border-white/10 rounded-2xl">
            <p className="text-xs text-hierarchy-4 leading-relaxed">
              <strong className="text-hierarchy-3">FDA Disclaimer:</strong> The statements made regarding these products have not been evaluated by the Food and Drug Administration. 
              The efficacy of these products has not been confirmed by FDA-approved research. These products are not 
              intended to diagnose, treat, cure or prevent any disease. All information presented here is not meant as 
              a substitute for or alternative to information from health care practitioners. Please consult your health 
              care professional about potential interactions or other possible complications before using any product. 
              The Federal Food, Drug and Cosmetic Act requires this notice.
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced gradient divider at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-eskerium-gold/80 to-transparent"></div>
    </footer>
  )
}